import * as React from 'react'

import { LOCAL_STORAGE_KEYS } from 'config/Vars'

import getMultiFilters from 'modules/core/components/MultiFilters/hooks/utils/getMultiFilters'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import {
  formatCustomFilters,
  getCustomFilters,
} from 'modules/shift_management/components/ShiftManagement/Content/_filters/customFilters'
import { isStatusWithoutConflict } from 'modules/shift_management/components/ShiftManagement/Content/_filters/customFilters'
import useApplyFilters from 'modules/shift_management/hooks/useApplyFilters'
import { useLoadBootstrapData } from 'modules/shift_management/hooks/useFetchEmployees'
import { useFilters } from 'modules/shift_management/hooks/useFilters'
import { useNotifications } from 'modules/shift_management/hooks/useNotifications'
import { usePagination } from 'modules/shift_management/hooks/usePagination'
import { usePermissions } from 'modules/shift_management/hooks/usePermissions'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { useUI } from 'modules/shift_management/hooks/useUI'
import Api from 'modules/shift_management/lib/Api'

const usePublishShifts = (contractVersions: ContractVersions) => {
  const [showPublishModal, togglePublishModal] = React.useState<boolean>(false)
  const [{ shiftsRows }, dispatchShiftsRows] = useShiftsRows()
  const [{ sendNotifications }] = useNotifications()
  const [{ view }, dispatchSchedule] = useSchedule()
  const [, dispatchPagination] = usePagination()
  const [, dispatchUI] = useUI()
  const [{ without_shifts_ids }] = useFilters()
  const [{ canManageShifts }] = usePermissions()

  const { loadBootstrapData } = useLoadBootstrapData(contractVersions)

  const { getFilteredEmployees } = useApplyFilters(shiftsRows, contractVersions)

  const { running, performAction: onConfirm } = useAsyncAction(
    async () => {
      const employeeIds = getFilteredEmployees().map((e) => e.id)

      const { filters: multiFilters, logicOperator } = getMultiFilters(
        LOCAL_STORAGE_KEYS.shiftEmployees
      )
      const custom_filters = getCustomFilters(multiFilters)
      const filterByWithoutConflict = isStatusWithoutConflict(multiFilters)

      await Api.publish(
        view.startAt,
        view.endAt,
        {
          employee_ids: employeeIds,
          without_shifts_ids: filterByWithoutConflict ? without_shifts_ids : null,
          logic_operator: logicOperator.get('logic_operator').value,
          custom_filters: formatCustomFilters(custom_filters),
        },
        sendNotifications,
        view.viewType
      )

      track(trackingEvents.shiftManagement.publishShiftManagementSubmit, {
        view: view.viewType,
        view_type_by: view.groupType,
        from_date: view.startAt,
        to_date: view.endAt,
        number_of_filters: multiFilters.length,
        send_notification: sendNotifications,
      })

      dispatchUI({ type: 'LOADING' })
      dispatchSchedule({ type: 'PUBLISH_WEEK' })
      dispatchPagination({ type: 'RESET_PAGINATION' })
      dispatchShiftsRows({ type: 'RESET_SHIFTS_ROWS' })

      togglePublishModal(false)

      loadBootstrapData()
    },
    {
      successMessage: i18n.t('shift_management.actions.publish_success'),
    }
  )

  return {
    submitting: running,
    showPublishModal: showPublishModal && canManageShifts,
    togglePublishModal,
    onConfirm,
    showPublishButton: canManageShifts,
  }
}

export default usePublishShifts
